/* eslint-disable i18next/no-literal-string */
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {EmployeeRoleLevel, WorkstreamOrphan, WorkstreamService} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {StatusChip} from 'components/DSYS/StatusChip';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useNavigate} from 'react-router';
import {getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const parseApiDateTime = (value: string) =>
  new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`);

const getSortableTime = (value?: string | null) => {
  if (!value) return 0;
  const time = parseApiDateTime(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const formatDate = (value?: string | null) =>
  getSortableTime(value) ? dateFormatter.format(parseApiDateTime(value as string)) : 'None';

const getStatusValue = (status?: string | null) => status || 'no_status';

const firstName = (people: EmployeeRoleLevel[]) => people[0]?.name ?? '';

const columns: Column<WorkstreamOrphan>[] = [
  {
    type: 'Workstream',
    fixed: true,
    minWidth: 320,
    sort: (a, b) => a.name.localeCompare(b.name),
  },
  {
    type: 'Departed DRI',
    minWidth: 200,
    sort: (a, b) => firstName(a.departed_dris).localeCompare(firstName(b.departed_dris)),
  },
  {
    type: 'Their manager',
    minWidth: 200,
    sort: (a, b) => firstName(a.managers).localeCompare(firstName(b.managers)),
  },
  {
    type: 'Exec sponsor',
    minWidth: 200,
    sort: (a, b) => firstName(a.exec_sponsors).localeCompare(firstName(b.exec_sponsors)),
  },
  {
    type: 'Latest status',
    minWidth: 160,
    sort: (a, b) =>
      getStatusValue(a.latest_update_status).localeCompare(getStatusValue(b.latest_update_status)),
  },
  {
    type: 'Latest update',
    minWidth: 140,
    sort: (a, b) => getSortableTime(a.latest_update_at) - getSortableTime(b.latest_update_at),
  },
];

const PersonLink = ({person}: {person: EmployeeRoleLevel}) => (
  <PersonHoverCard ldap={person.ldap} name={person.name} role={person.role}>
    <Link
      to={ROUTE_PATHS.PROFILE.replace(':userid', person.ldap)}
      monochromatic
      hasNoUnderline
      showUnderlineOnHover
      style={{display: 'inline-flex', alignItems: 'center', gap: 6}}
    >
      <Avatar user={person} size="xsmall" />
      <Text>{person.name}</Text>
    </Link>
  </PersonHoverCard>
);

// Departed people's profiles are gone, so show their names without links.
const People = ({people, linked}: {people: EmployeeRoleLevel[]; linked: boolean}) =>
  people.length === 0 ? (
    <Text color="subtle">None</Text>
  ) : (
    <Stack gap="4">
      {people.map((person) =>
        linked ? (
          <PersonLink key={person.user_id} person={person} />
        ) : (
          <Text key={person.user_id}>{person.name}</Text>
        )
      )}
    </Stack>
  );

const OrphanRow = ({orphan}: {orphan: WorkstreamOrphan}) => (
  <Table.Row isSelectable>
    <Table.Cell>
      <Link
        to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(orphan.id))}
        monochromatic
        hasNoUnderline
        showUnderlineOnHover
      >
        <Text>{orphan.name}</Text>
      </Link>
    </Table.Cell>
    <Table.Cell>
      <People people={orphan.departed_dris} linked={false} />
    </Table.Cell>
    <Table.Cell>
      <People people={orphan.managers} linked />
    </Table.Cell>
    <Table.Cell>
      <People people={orphan.exec_sponsors} linked />
    </Table.Cell>
    <Table.Cell>
      <StatusChip
        status={getStatusValue(orphan.latest_update_status)}
        asStatus={getWorkstreamUpdateVisualStatus(orphan.latest_update_status)}
        includeArchived
      />
    </Table.Cell>
    <Table.Cell>
      <Text>{formatDate(orphan.latest_update_at)}</Text>
    </Table.Cell>
  </Table.Row>
);

export const AdminWorkstreamOrphans = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  useDocumentTitle('Orphaned workstreams');

  const {data, isError, isLoading} = useQuery({
    queryKey: ['admin', 'workstream-orphans'],
    queryFn: WorkstreamService.getAdminOrphanedWorkstreamsApiV1WorkstreamAdminOrphanedGet,
  });

  const orphans = data?.workstreams ?? [];

  if (!pulseUser) {
    return null;
  }

  if (!isUserAdmin(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Orphaned workstreams', to: ROUTE_PATHS.ADMIN_WORKSTREAM_ORPHANS},
      ]}
    >
      <Stack gap="24">
        <Stack gap="8">
          <Title size={24}>Orphaned workstreams</Title>
          <Text color="subtle">
            Open workstreams whose DRIs have all left. People count as departed once the directory
            sync marks them inactive. Their manager is the nearest active person above them.
          </Text>
          {data ? (
            <Text color="subtle">
              {orphans.length} workstream{orphans.length === 1 ? '' : 's'}
            </Text>
          ) : null}
        </Stack>

        <Table
          columns={columns}
          data={orphans}
          sortConfig={{columnId: 'Latest update', asc: false}}
          compact
        >
          <Table.Header />
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box display="flex" alignItems="center" style={{gap: 8}}>
                    <Spinner size="small" />
                    <Text color="subtle">Loading workstreams</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : isError ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">Unable to load workstreams.</Text>
                </Table.Cell>
              </Table.Row>
            ) : orphans.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">Every open workstream has an active DRI.</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              <Table.Rows renderRow={(orphan) => <OrphanRow orphan={orphan} />} />
            )}
          </Table.Body>
        </Table>
      </Stack>
    </Layout.Container>
  );
};
